import React from "react";
import katex from "katex";
import "katex/dist/katex.min.css";
import { v4 as uuidv4 } from "uuid";

// ✅ Thêm hàm này trong MathRendererContent
const enhanceIframes = (html = "") => {
  return html.replace(/<iframe\b([^>]*)>/gi, (match, attrs = "") => {
    let a = attrs;

    // add allowfullscreen
    if (!/allowfullscreen/i.test(a)) a += " allowfullscreen";

    // add allow=... (nếu chưa có)
    if (!/\sallow=/i.test(a)) {
      a +=
        ' allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"';
    } else {
      // nếu đã có allow="" mà thiếu fullscreen thì append
      a = a.replace(/allow=(["'])([\s\S]*?)\1/i, (m, q, v) => {
        if (/fullscreen/i.test(v)) return m;
        return `allow=${q}${v}; fullscreen${q}`;
      });
    }

    return `<iframe${a}>`;
  });
};

const decodeHtmlEntities = (input) => {
  if (!input) return "";

  if (typeof window === "undefined" || !document?.createElement) {
    return input
      .replaceAll("&nbsp;", " ")
      .replaceAll("&lt;", "<")
      .replaceAll("&gt;", ">")
      .replaceAll("&amp;", "&")
      .replaceAll("&quot;", '"')
      .replaceAll("&#39;", "'")
      .replaceAll("&apos;", "'");
  }

  const textarea = document.createElement("textarea");
  textarea.innerHTML = input;
  return textarea.value;
};

// ✅ Collapse nhiều <br> liên tiếp về 1 <br> để tránh giãn dòng bất thường
const collapseBR = (html = "") => {
  // thay mọi dạng <br>, <br/>, <br /> ... thành <br>
  let s = html.replace(/<br\s*\/?>/gi, "<br>");
  // collapse 2+ <br> liên tiếp thành 1 <br>
  s = s.replace(/(?:\s*<br>\s*){2,}/gi, "<br>");
  return s;
};

// Chuẩn hóa HTML sinh từ CKEditor (giảm ảnh hưởng của <p> tới hiển thị LaTeX)
const normalizeHtmlForPreview = (html) => {
  if (!html) return "";
  let result = html;

  // Ghép các đoạn </p><p> thành 1 dòng với <br>
  result = result.replace(/<\/p>\s*<p[^>]*>/gi, "<br>");

  // Bỏ <p> bao ngoài cùng (nếu có)
  result = result.replace(/^<p[^>]*>/i, "").replace(/<\/p>$/i, "");

  // ✅ collapse nhiều <br> để không bị cách dòng quá xa
  result = collapseBR(result);

  return result;
};

const MathRendererContent = ({ content }) => {
  const parseContent = (text) => {
    if (!text) return [];

    const normalized =
      typeof text === "string" ? normalizeHtmlForPreview(text) : text;

    const decodedText = decodeHtmlEntities(normalized).replace(/\u00A0/g, " ");

    // Kiểm tra nếu content chứa HTML tags
    const hasHtmlTags = /<[^>]*>/g.test(decodedText);

    if (hasHtmlTags) {
      return parseHtmlContent(decodedText);
    }
    return parseTextContent(decodedText);
  };

  // ✅ Render LaTeX trực tiếp vào CHUỖI HTML (KHÔNG tách node) => không vỡ <table>/<td>
  const renderLatexInsideHtmlString = (htmlText) => {
    const regex = /\$\$([\s\S]*?)\$\$|\$([\s\S]*?)\$/g;

    let out = "";
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(htmlText)) !== null) {
      out += htmlText.substring(lastIndex, match.index);

      const rawFormula = match[1] ?? match[2];
      const forceDisplay = !!match[1];
      const formula = processLatexFormula(rawFormula);

      try {
        // ✅ chỉ display nếu người dùng dùng $$...$$ hoặc môi trường block “cứng”
        const isHardBlockEnv =
          /\\begin\{(align|align\*|equation|equation\*|gather|gather\*|multline|multline\*)\}/.test(
            formula
          );

        const displayMode = forceDisplay || isHardBlockEnv;

        const katexHtml = katex.renderToString(formula, {
          throwOnError: false,
          displayMode,
        });

        out += katexHtml;
      } catch (e) {
        console.error("Error rendering LaTeX (HTML mode):", e);
        // fallback: giữ nguyên text công thức
        out += match[0];
      }

      lastIndex = regex.lastIndex;
    }

    out += htmlText.substring(lastIndex);
    return out;
  };

  const parseHtmlContent = (htmlText) => {
    // Thay thế các ký hiệu latex block như \[...\] bằng $$...$$
    htmlText = htmlText.replaceAll("\\[", "$$");
    htmlText = htmlText.replaceAll("\\]", "$$");

    htmlText = enhanceIframes(htmlText);

    // ✅ collapse br toàn cục để tránh nhiều dòng trống
    htmlText = collapseBR(htmlText);

    // Clean up br tags ở đầu/cuối
    htmlText = htmlText.replace(/^((<br\s*\/?>)+)/gi, "");
    htmlText = htmlText.replace(/(\s*<br\s*\/?>\s*)+$/gi, "");

    // Thay thế bullet points
    htmlText = htmlText
      .replace(/•/g, "▪")
      .replace(/&#8226;/g, "▪")
      .replace(/&bull;/g, "▪")
      .replace(/&#x2022;/g, "▪")
      .replace(/&#x25AA;/g, "▪")
      .replace(/\u25A1/g, "▫");

    // ✅ FIX TRIỆT ĐỂ: render latex ngay trong html string
    const renderedHtml = renderLatexInsideHtmlString(htmlText);

    // ✅ table không được nằm trong <span> => dùng div nếu có table
    const hasTable = /<\s*table\b/i.test(renderedHtml);
    const WrapperTag = hasTable ? "div" : "span";

    return [
      <WrapperTag
        key={uuidv4()}
        dangerouslySetInnerHTML={{ __html: renderedHtml }}
      />,
    ];
  };

  const parseTextContent = (text) => {
    // Thay thế các ký hiệu latex block như \[...\] bằng $$...$$
    text = text.replaceAll("\\[", "$$");
    text = text.replaceAll("\\]", "$$");

    // Đảm bảo xuống dòng giữ được bằng <br/>
    text = text.replace(/\n/g, "<br/>");

    // ✅ collapse nhiều <br/> liên tiếp để tránh giãn dòng
    text = text.replace(/(?:\s*<br\/>\s*){2,}/gi, "<br/>");

    const regex = /\$\$([\s\S]*?)\$\$|\$([\s\S]*?)\$/g;

    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      // Text trước công thức
      if (match.index > lastIndex) {
        const plainText = text.substring(lastIndex, match.index);

        if (plainText === "<br/>") {
          parts.push(<br key={`line-${uuidv4()}`} />);
        } else if (plainText?.includes("<br/>")) {
          const arr = plainText.split("<br/>");
          arr.forEach((line, index) => {
            if (line) parts.push(line);
            if (index !== arr.length - 1) {
              parts.push(<br key={`line-${uuidv4()}`} />);
            }
          });
        } else {
          parts.push(plainText);
        }
      }

      const rawFormula = match[1] ?? match[2];
      const forceDisplay = !!match[1];
      const formula = processLatexFormula(rawFormula);

      try {
        // ✅ FIX: KHÔNG ép cases/aligned/matrix/\\\\ thành display nữa
        // chỉ display khi $$...$$ hoặc block env cứng
        const isHardBlockEnv =
          /\\begin\{(align|align\*|equation|equation\*|gather|gather\*|multline|multline\*)\}/.test(
            formula
          );

        const displayMode = forceDisplay || isHardBlockEnv;

        const html = katex.renderToString(formula, {
          throwOnError: false,
          displayMode,
        });

        parts.push(
          <span key={uuidv4()} dangerouslySetInnerHTML={{ __html: html }} />
        );
      } catch (error) {
        console.error("Error rendering LaTeX:", error);
        parts.push(formula);
      }

      lastIndex = regex.lastIndex;
    }

    // Text còn lại
    if (lastIndex < text?.length) {
      const remainingText = text.substring(lastIndex);

      if (remainingText === "<br/>") {
        parts.push(<br key={`line-${uuidv4()}`} />);
      } else if (remainingText?.includes("<br/>")) {
        const arr = remainingText.split("<br/>");
        arr.forEach((line, index) => {
          if (line) parts.push(line);
          if (index !== arr.length - 1) {
            parts.push(<br key={`line-${uuidv4()}`} />);
          }
        });
      } else {
        parts.push(remainingText);
      }
    }

    return parts;
  };

  const processLatexFormula = (formula) => {
    formula = formula
      .replace(/<br\s*\/?>/gi, "")
      .replace(/<[^>]+>/g, "")
      .replace(/\r?\n/g, " ");

    const pattern = /\\begin\{array\}\{\*\{20\}\{l\}\}/g;
    const matches = formula.match(pattern);

    if (
      matches &&
      matches.length < 2 &&
      formula.includes("\\begin{array}{*{20}{l}}")
    ) {
      formula = formula.replaceAll(
        "end{array} \\Leftrightarrow",
        "end{array} \\Leftrightarrow \\newline"
      );
      formula = formula.replaceAll(
        "end{array} \\Rightarrow",
        "end{array} \\Rightarrow \\newline"
      );
      formula = formula.replaceAll(
        "end{array}} \\right.} \\right.",
        "end{array} \\right. "
      );
      formula = formula.replaceAll(
        "{\\begin{array}{*{20}{l}}",
        "\\begin{array}{l}"
      );
      formula = formula.replaceAll("end{array}}", "end{array}");
    } else if (
      formula.includes("\\begin{array}{*{20}{l}}") ||
      formula.includes("{\\begin{array}{*{20}{c}}") ||
      formula.includes("{\\begin{array}{*{20}{r}}")
    ) {
      formula = formula.replaceAll("\\left\\{", "");
      formula = formula.replaceAll(/\\left\[/g, "");
      formula = formula.replaceAll("} \\right.}", "");
      formula = formula.replaceAll("\\right.}", "");
      formula = formula.replaceAll(/\\right\./g, "");
      formula = formula.replaceAll(
        "{\\begin{array}{*{20}{l}}",
        "\\begin{cases}"
      );
      formula = formula.replaceAll(
        "{\\begin{array}{*{20}{c}}",
        "\\begin{cases}"
      );
      formula = formula.replaceAll(
        "{\\begin{array}{*{20}{r}}",
        "\\begin{cases}"
      );
      formula = formula.replaceAll("end{array}}", "end{cases}");
    }

    formula = formula
      .replaceAll("<br/>", "")
      .replaceAll("<p>", "")
      .replaceAll("</p>", "")
      .replaceAll("^^\\circ", "^\\circ")
      .replaceAll("&lt;", "<")
      .replaceAll("&gt;", ">")
      .replaceAll("\\mathop \\smallint \\nolimits^", "\\int")
      .replaceAll("\\right)}^", "\\right)}")
      .replaceAll("C)^'}", "C)'}");

    return formula;
  };

  return (
    <div
      style={{
        fontFamily: "inherit",
        lineHeight: "1.45",
        fontSize: "16px",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
      }}
    >
      <style>
        {`
          .math-renderer {
            white-space: normal; /* ✅ tránh cảm giác giãn kỳ lạ */
          }

          /* ✅ Nếu HTML còn sót p/div thì bỏ margin để không tạo khoảng trống */
          .math-renderer p,
          .math-renderer div {
            margin: 0;
          }

          /* ✅ Canh trái + giảm margin block math để không bị xa */
          .math-renderer .katex-display{
            text-align: left !important;
            margin: 0.2em 0 !important;
          }
          .math-renderer .katex-display > .katex{
            text-align: left !important;
          }

          /* ✅ giảm cảm giác "cao dòng" trong katex */
          .math-renderer .katex {
            font-size: 1.05em;
            line-height: 1.25;
          }

         /* ==========================
            ✅ FIX TRIỆT ĐỂ TABLE BORDER
            - Word thường xuất ra: <table class="word-table">...<td>...
            - App thường có CSS reset table/td border: none
            => dùng selector có scope + !important
          =========================== */

          .math-renderer table,
          .math-renderer .word-table {
            border-collapse: collapse !important;
            border-spacing: 0 !important;

            margin: 10px auto !important;
            width: auto !important;
            max-width: 100% !important;
            background: #fff !important;

            /* ✅ outer border chắc chắn hiện đủ 4 cạnh */
            border: none !important;
 
            outline-offset: 0 !important;

            overflow: hidden !important;
            box-shadow: 0 1px 6px rgba(0,0,0,0.08) !important;
          }

          /* cell */
          .math-renderer th,
          .math-renderer td,
          .math-renderer .word-table th,
          .math-renderer .word-table td {
            border: 1px solid #2f2f2f !important;
            padding: 10px 14px !important;
            vertical-align: middle !important;
            text-align: center !important; /* số liệu canh giữa */
            font-size: 15px !important;
          }

          /* header */
          .math-renderer th,
          .math-renderer .word-table th {
            font-weight: 700 !important;
            background: #f3f4f6 !important;
          }

          /* row zebra nhẹ (nếu bảng nhiều dòng) */
          .math-renderer tr:nth-child(even) td,
          .math-renderer .word-table tr:nth-child(even) td {
            background: #fafafa !important;
          }

          /* responsive: nhỏ thì giảm padding */
          @media (max-width: 768px) {
            .math-renderer th,
            .math-renderer td,
            .math-renderer .word-table th,
            .math-renderer .word-table td {
              padding: 7px 9px !important;
              font-size: 13px !important;
            }
          }
        `}
      </style>

      <div className="math-renderer">
        {parseContent(content)
          .map((part, index) => {
            if (typeof part === "string" || typeof part === "number") {
              return <span key={index}>{part}</span>;
            }
            return <React.Fragment key={index}>{part}</React.Fragment>;
          })
          .filter(Boolean)}
      </div>
    </div>
  );
};

export default MathRendererContent;
